/**
 * UserRosterPage — /users (Phase 4.3 / rubric O)
 *
 * Paginated, searchable roster of all user profiles with an account-lifecycle
 * drill-down drawer. Row click opens a drawer showing the profile detail plus
 * Suspend / Reinstate / Disable / Restore actions — each gated on the current
 * accountState and each prompting for a reason (min 10 chars) before it fires.
 * Actions refresh the roster optimistically and surface a Toast.
 *
 * Mirrors JobHistoryPage (Table + Drawer + Toast + useSearchParams URL state)
 * and the CostAnalysisPage user sub-view ("View job history →" link).
 *
 * NOTE: board history, agent-activity, and credit ledger for a user are each
 * surfaced through their own existing admin routes (/jobs?profileId=…,
 * /cost?view=user). This page links out to them rather than duplicating a
 * backend.
 */

import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  getUsers,
  suspendUser,
  reinstateUser,
  disableUser,
  restoreUser,
  ProfileNotFoundError,
  type AdminUserRosterItemDto,
} from '../api/adminProfileClient';
import { COLDSTART_RETRY_CONFIG } from '../api/resilientFetch';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { Table, type Column } from '../components/Table';
import { Drawer } from '../components/Drawer';
import { Toast, type ToastState } from '../components/Toast';

const PAGE_SIZE = 50;
const MIN_REASON_LEN = 10;

// ---------------------------------------------------------------------------
// Account State + Tier filters
//
// getUsers() (adminProfileClient) only accepts search/page/pageSize today —
// the Profile.API admin roster endpoint has no accountState/tier query
// params. Until that ships server-side, these filters apply client-side over
// the currently loaded page (labeled below), mirroring ProvidersPage's
// status-filter UX.
// ---------------------------------------------------------------------------

type AccountStateFilter = 'All' | 'Guest' | 'Pending' | 'Active' | 'Suspended' | 'Disabled';
const ACCOUNT_STATE_FILTERS: AccountStateFilter[] = [
  'All',
  'Guest',
  'Pending',
  'Active',
  'Suspended',
  'Disabled',
];

type TierFilter = 'All' | 'free' | 'basic' | 'pro' | 'premium';
const TIER_FILTERS: TierFilter[] = ['All', 'free', 'basic', 'pro', 'premium'];

// ---------------------------------------------------------------------------
// Lifecycle actions
// ---------------------------------------------------------------------------

type LifecycleAction = 'suspend' | 'reinstate' | 'disable' | 'restore';

const ACTION_LABELS: Record<LifecycleAction, string> = {
  suspend: 'Suspend',
  reinstate: 'Reinstate',
  disable: 'Disable',
  restore: 'Restore',
};

const ACTION_FNS: Record<LifecycleAction, (id: string, reason: string) => Promise<void>> = {
  suspend: suspendUser,
  reinstate: reinstateUser,
  disable: disableUser,
  restore: restoreUser,
};

/**
 * Which actions are enabled for a given accountStateName. Names come straight
 * from the API's AccountState enum: Guest, Pending, Active, Suspended, Disabled.
 */
function enabledActions(accountStateName: string): Record<LifecycleAction, boolean> {
  const s = accountStateName.toLowerCase();
  const isSuspended = s === 'suspended';
  const isDisabled = s === 'disabled';
  return {
    // Can't suspend someone already suspended or hard-disabled.
    suspend: !isSuspended && !isDisabled,
    // Reinstate only makes sense out of the suspended state.
    reinstate: isSuspended,
    // Disable anyone not already disabled.
    disable: !isDisabled,
    // Restore only a disabled account.
    restore: isDisabled,
  };
}

// ---------------------------------------------------------------------------
// State badge
// ---------------------------------------------------------------------------

function StateBadge({ name }: { name: string }) {
  const colours: Record<string, string> = {
    guest: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40',
    pending: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    suspended: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    disabled: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  const cls = colours[name.toLowerCase()] ?? 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium capitalize ${cls}`}>
      {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function displayLabel(u: AdminUserRosterItemDto): string {
  return (
    u.displayName?.trim() ||
    [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
    u.email ||
    u.profileId
  );
}

// ---------------------------------------------------------------------------
// Reason modal — mounted only while a target action is pending, so its local
// state resets naturally on each open (no set-state-in-effect).
// ---------------------------------------------------------------------------

function ReasonModal({
  actionLabel,
  userLabel,
  onConfirm,
  onCancel,
}: {
  actionLabel: string;
  userLabel: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const valid = reason.trim().length >= MIN_REASON_LEN;

  async function submit() {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${actionLabel} account`}
        className="relative z-10 w-full max-w-md rounded-xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl"
      >
        <h2 className="text-sm font-semibold text-zinc-100">
          {actionLabel} account
        </h2>
        <p className="mt-1 text-xs text-zinc-400 break-words">{userLabel}</p>

        <label className="mt-4 block text-xs text-zinc-400" htmlFor="lifecycle-reason">
          Reason (required, min {MIN_REASON_LEN} characters)
        </label>
        <textarea
          id="lifecycle-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Why is this action being taken?"
          className="mt-1 w-full px-3 py-2 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 resize-y"
        />
        {!valid && reason.length > 0 && (
          <p className="mt-1 text-xs text-amber-400">
            {MIN_REASON_LEN - reason.trim().length > 0
              ? `${MIN_REASON_LEN - reason.trim().length} more character(s) needed.`
              : ''}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            disabled={!valid || saving}
            onClick={() => void submit()}
            className="flex-1 min-h-[48px] px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-zinc-950 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {saving ? 'Working…' : `Confirm ${actionLabel}`}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="flex-1 min-h-[48px] px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drawer body — keyed by profileId so it remounts (and resets) on new target.
// ---------------------------------------------------------------------------

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-zinc-500 text-xs">{label}</span>
      <p className="text-zinc-300 text-xs break-words">{value ?? '—'}</p>
    </div>
  );
}

function UserDetailBody({
  user,
  isPending,
  onAction,
}: {
  user: AdminUserRosterItemDto;
  isPending: boolean;
  onAction: (action: LifecycleAction) => void;
}) {
  const navigate = useNavigate();
  const enabled = enabledActions(user.accountStateName);
  const actions: LifecycleAction[] = ['suspend', 'reinstate', 'disable', 'restore'];

  return (
    <div className="space-y-4">
      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-3">
        <DetailField label="Display Name" value={user.displayName} />
        <DetailField label="Email" value={user.email} />
        <DetailField label="First Name" value={user.firstName} />
        <DetailField label="Last Name" value={user.lastName} />
        <DetailField label="Account State" value={<StateBadge name={user.accountStateName} />} />
        <DetailField label="Subscription Tier" value={user.subscriptionTier} />
        <DetailField label="Created" value={formatDate(user.createdAt)} />
        <DetailField label="Deleted" value={formatDate(user.deletedAt)} />
        <DetailField
          label="Profile ID"
          value={<span className="font-mono break-all">{user.profileId}</span>}
        />
      </div>

      {/* Lifecycle actions */}
      <div className="border-t border-zinc-800 pt-4">
        <p className="text-xs font-semibold text-zinc-400 mb-2">Account lifecycle</p>
        <div className="grid grid-cols-2 gap-2">
          {actions.map((action) => {
            const isEnabled = enabled[action] && !isPending;
            const danger = action === 'suspend' || action === 'disable';
            return (
              <button
                key={action}
                type="button"
                disabled={!isEnabled}
                onClick={() => onAction(action)}
                aria-label={`${ACTION_LABELS[action]} account`}
                className={[
                  'min-h-[48px] px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
                  danger
                    ? 'border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white',
                ].join(' ')}
              >
                {ACTION_LABELS[action]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cross-links to existing admin routes */}
      <div className="border-t border-zinc-800 pt-4 space-y-2">
        <p className="text-xs font-semibold text-zinc-400 mb-1">Related</p>
        <button
          type="button"
          onClick={() => navigate(`/jobs?profileId=${encodeURIComponent(user.profileId)}`)}
          className="w-full min-h-[48px] px-3 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-xs font-medium hover:bg-zinc-800 hover:text-zinc-100 transition-colors text-left"
        >
          View job history →
        </button>
        <button
          type="button"
          onClick={() =>
            navigate(`/cost?view=user&profileId=${encodeURIComponent(user.profileId)}`)
          }
          className="w-full min-h-[48px] px-3 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-xs font-medium hover:bg-zinc-800 hover:text-zinc-100 transition-colors text-left"
        >
          View credits &amp; cost →
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function UserRosterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openUserId = searchParams.get('userId');

  // Filters / paging
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [accountStateFilter, setAccountStateFilter] = useState<AccountStateFilter>(() => {
    const s = searchParams.get('accountState');
    return (ACCOUNT_STATE_FILTERS as string[]).includes(s ?? '') ? (s as AccountStateFilter) : 'All';
  });
  const [tierFilter, setTierFilter] = useState<TierFilter>(() => {
    const t = searchParams.get('tier');
    return (TIER_FILTERS as string[]).includes(t ?? '') ? (t as TierFilter) : 'All';
  });
  const [page, setPage] = useState(1);

  // Data
  const [users, setUsers] = useState<AdminUserRosterItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Action state
  const [actionTarget, setActionTarget] = useState<{ user: AdminUserRosterItemDto; action: LifecycleAction } | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const dismissToast = useCallback(() => setToast(null), []);

  // Debounce the search box into the query.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Work items 21761 / 20684: Profile.API's cold first call measured 13.4-14.2 s on DEV,
  // over the 10 s default attempt timeout, so only the first successful load gets the
  // cold-start budget; search/page reloads stay on the warm default.
  const loadedOnce = useRef(false);

  const beginLoad = useLatestRequest();

  const fetchUsers = useCallback(() => {
    const isCurrent = beginLoad();
    setIsLoading(true);
    setError(null);
    getUsers(
      { search: search || undefined, page, pageSize: PAGE_SIZE },
      loadedOnce.current ? undefined : COLDSTART_RETRY_CONFIG,
    )
      .then((result) => {
        if (!isCurrent()) return;
        loadedOnce.current = true;
        setUsers(result.items);
        setTotal(result.total);
      })
      .catch((err: unknown) => {
        if (!isCurrent()) return;
        setError(err instanceof Error ? err.message : 'Failed to load users');
      })
      .finally(() => {
        if (isCurrent()) setIsLoading(false);
      });
  }, [beginLoad, search, page]);

  useEffect(() => {
    startTransition(() => { void fetchUsers(); });
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (accountStateFilter !== 'All' && u.accountStateName !== accountStateFilter) return false;
      if (tierFilter !== 'All' && u.subscriptionTier.toLowerCase() !== tierFilter) return false;
      return true;
    });
  }, [users, accountStateFilter, tierFilter]);

  const isFiltered = accountStateFilter !== 'All' || tierFilter !== 'All';

  function selectAccountState(filter: AccountStateFilter) {
    setAccountStateFilter(filter);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (filter === 'All') next.delete('accountState');
      else next.set('accountState', filter);
      return next;
    });
  }

  function selectTier(filter: TierFilter) {
    setTierFilter(filter);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (filter === 'All') next.delete('tier');
      else next.set('tier', filter);
      return next;
    });
  }

  function openDrawer(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('userId', id);
      return next;
    });
  }

  function closeDrawer() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('userId');
      return next;
    });
  }

  async function handleConfirmAction(reason: string) {
    if (!actionTarget) return;
    const { user, action } = actionTarget;
    setActionPending(true);
    try {
      await ACTION_FNS[action](user.profileId, reason);
      setToast({ variant: 'success', message: `${ACTION_LABELS[action]} applied to ${displayLabel(user)}` });
      setActionTarget(null);
      // Optimistic refresh — the roster reflects the new accountState on reload.
      void fetchUsers();
    } catch (err) {
      if (err instanceof ProfileNotFoundError) {
        setToast({ variant: 'error', message: 'Profile not found — it may have been removed.' });
      } else {
        setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Action failed' });
      }
    } finally {
      setActionPending(false);
    }
  }

  const selectedUser =
    openUserId !== null ? users.find((u) => u.profileId === openUserId) ?? null : null;

  const columns: Column<AdminUserRosterItemDto>[] = [
    {
      key: 'user',
      label: 'User',
      render: (row) => (
        <div className="min-w-0">
          <p className="text-xs text-zinc-200 truncate max-w-xs" title={displayLabel(row)}>
            {displayLabel(row)}
          </p>
          {row.email && row.email !== displayLabel(row) && (
            <p className="text-xs text-zinc-500 truncate max-w-xs" title={row.email}>
              {row.email}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'accountState',
      label: 'State',
      render: (row) => <StateBadge name={row.accountStateName} />,
    },
    {
      key: 'subscriptionTier',
      label: 'Tier',
      render: (row) => <span className="text-xs text-zinc-300 capitalize">{row.subscriptionTier}</span>,
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => <span className="text-xs font-mono text-zinc-500">{formatDate(row.createdAt)}</span>,
    },
  ];

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Header + search bar */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-zinc-800 bg-zinc-950">
        <div className="mr-2">
          <h1 className="text-sm font-semibold text-zinc-100">User Roster</h1>
          <p className="text-xs text-zinc-500">Search users and manage account lifecycle</p>
        </div>

        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name or email…"
          aria-label="Search users"
          className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 min-h-[48px] w-64"
        />

        <div className="flex rounded-lg border border-zinc-700 overflow-hidden" role="group" aria-label="Filter by account state">
          {ACCOUNT_STATE_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => selectAccountState(filter)}
              aria-pressed={accountStateFilter === filter}
              className={[
                'px-3 py-1.5 text-xs font-medium min-h-[48px] transition-colors',
                accountStateFilter === filter
                  ? 'bg-violet-700 text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
              ].join(' ')}
            >
              {filter}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1.5 text-xs text-zinc-400">
          Tier
          <select
            value={tierFilter}
            onChange={(e) => selectTier(e.target.value as TierFilter)}
            aria-label="Filter by subscription tier"
            className="px-2 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 focus:outline-none focus:border-violet-500 min-h-[48px] capitalize"
          >
            {TIER_FILTERS.map((t) => (
              <option key={t} value={t} className="capitalize">
                {t}
              </option>
            ))}
          </select>
        </label>

        <div className="ml-auto text-right">
          <p className="text-xs text-zinc-500">
            {isFiltered
              ? `${filteredUsers.length.toLocaleString()} of ${total.toLocaleString()} users (filtered on current page)`
              : `${total.toLocaleString()} users`}
          </p>
          {/* Bug 15182: three different "user" totals appear across this console with no
              scoping — 8,718 here, 14,201 on /dashboard, 51,108 on /reports/billing. This
              roster reads directly from Profile.API/ProfileDB, the identity service's own
              table, making it the most trustworthy of the three. Say so at the point of
              display. */}
          <p className="text-[10px] text-zinc-600">Registered profiles — ProfileDB, the identity source of truth</p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-6 py-2 bg-red-500/10 border-b border-red-500/20 text-red-300 text-xs">
          {error}
        </div>
      )}

      {isFiltered && (
        <div className="px-6 py-2 bg-zinc-900 border-b border-zinc-800 text-xs text-zinc-500">
          Account State / Tier filters apply to the currently loaded page only — the roster API does
          not yet support server-side filtering by these fields. Narrow with search first for best results.
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <Table
          columns={columns}
          rows={filteredUsers}
          keyFn={(row) => row.profileId}
          onRowClick={(row) => openDrawer(row.profileId)}
          isLoading={isLoading}
          emptyMessage="No users match this search."
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </div>

      {/* Detail drawer */}
      <Drawer
        open={!!selectedUser}
        onClose={closeDrawer}
        title={
          selectedUser ? (
            <div className="space-y-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-100 truncate" title={displayLabel(selectedUser)}>
                {displayLabel(selectedUser)}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <StateBadge name={selectedUser.accountStateName} />
                <span className="text-xs text-zinc-500 capitalize">{selectedUser.subscriptionTier}</span>
              </div>
            </div>
          ) : (
            'User'
          )
        }
      >
        {selectedUser && (
          <UserDetailBody
            key={selectedUser.profileId}
            user={selectedUser}
            isPending={actionPending}
            onAction={(action) => setActionTarget({ user: selectedUser, action })}
          />
        )}
      </Drawer>

      {/* Reason modal */}
      {actionTarget && (
        <ReasonModal
          actionLabel={ACTION_LABELS[actionTarget.action]}
          userLabel={displayLabel(actionTarget.user)}
          onConfirm={handleConfirmAction}
          onCancel={() => setActionTarget(null)}
        />
      )}

      {/* Toast */}
      <Toast toast={toast} onDismiss={dismissToast} />
    </main>
  );
}
